import { doAjaxRequest, generateCsrfHeaders, withApiErrorHandling } from '@/common/utility'
import type {
  CreditSearchApiResponse,
  CreditSearchConfiguration,
  CreditSearchCourseResponse,
  CreditSearchInstitution,
  CreditSearchSubjectResponse,
} from '@/models/credit-search/api'
import type { CreditSearchPreviewData, CreditSearchPreviewPayload } from '@/models/credit-search/preview'

const pageSize = 50

type ResultRequest = {
  institutionId: number | null
  search: string
  offset: number
}

function resultQuery(request: ResultRequest): string {
  const params = new URLSearchParams({
    institutionId: request.institutionId === null ? '' : String(request.institutionId),
    search: request.search,
    limit: String(pageSize),
    offset: String(request.offset),
  })
  return params.toString()
}

export function useCreditSearchApi() {
  let courseRequestSequence = 0
  let subjectRequestSequence = 0

  function getConfig(): Promise<CreditSearchConfiguration> {
    return withApiErrorHandling(
      doAjaxRequest<CreditSearchConfiguration>('GET', 'calc/config', null, generateCsrfHeaders()),
    ).then((response) => response.data)
  }

  function getInstitutions(): Promise<CreditSearchInstitution[]> {
    const query = new URLSearchParams({
      show_empty_institutions: 'false',
      show_empty_countries: 'false',
    })
    return withApiErrorHandling(
      doAjaxRequest<CreditSearchInstitution[]>(
        'GET',
        `public/institution/list?${query.toString()}`,
        null,
        generateCsrfHeaders(),
      ),
    ).then((response) => response.data)
  }

  function getCourses(
    institutionId: number | null,
    search: string,
    offset: number,
  ): Promise<CreditSearchCourseResponse | null> {
    const sequence = ++courseRequestSequence
    const query = resultQuery({ institutionId, search, offset })
    return withApiErrorHandling(
      doAjaxRequest<CreditSearchCourseResponse>(
        'GET',
        `calc/external/course/list?${query}`,
        null,
        generateCsrfHeaders(),
      ),
    ).then(
      (response) => sequence === courseRequestSequence ? response.data : null,
      (error: Error) => sequence === courseRequestSequence ? Promise.reject(error) : null,
    )
  }

  function getSubjects(
    institutionId: number | null,
    search: string,
    offset: number,
  ): Promise<CreditSearchSubjectResponse | null> {
    const sequence = ++subjectRequestSequence
    const query = resultQuery({ institutionId, search, offset })
    return withApiErrorHandling(
      doAjaxRequest<CreditSearchSubjectResponse>(
        'GET',
        `calc/external/subject/list?${query}`,
        null,
        generateCsrfHeaders(),
      ),
    ).then(
      (response) => sequence === subjectRequestSequence ? response.data : null,
      (error: Error) => sequence === subjectRequestSequence ? Promise.reject(error) : null,
    )
  }

  function getPreview(payload: CreditSearchPreviewPayload): Promise<CreditSearchPreviewData> {
    const body = new URLSearchParams({ submission_data: JSON.stringify(payload) })
    return withApiErrorHandling(
      doAjaxRequest<CreditSearchPreviewData>('POST', 'calc/submission/preview', body, generateCsrfHeaders()),
    ).then((response) => response.data)
  }

  return { getConfig, getInstitutions, getCourses, getSubjects, getPreview, pageSize }
}

export type CreditSearchApi = ReturnType<typeof useCreditSearchApi>
export type CreditSearchConfigResponse = CreditSearchApiResponse<CreditSearchConfiguration>
